import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import {
  MdAdd,
  MdArrowDropDown,
  MdCheck,
  MdClose,
  MdDownload,
  MdFilePresent,
  MdLock,
  MdPictureAsPdf,
  MdRemove,
  MdRotateLeft,
  MdRotateRight,
  MdUploadFile,
} from 'react-icons/md';
import { readFileAsArrayBuffer } from '../utils/pdfExport';
import { exportRotatedPdf } from '../utils/rotateExport';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// ── Constants ─────────────────────────────────────────────────────────────────

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200];
const BASE_CANVAS_W = 148;
const uid = () => Math.random().toString(36).slice(2, 9);

/** Normalize any degree value to 0 | 90 | 180 | 270. */
const normalizeRotation = (deg) => ((Math.round(deg) % 360) + 360) % 360;

// ── RotatePageTile ────────────────────────────────────────────────────────────
// Non-sortable thumbnail with selection circle + rotation badge.

const RotatePageTile = ({
  page,
  pdfDoc,
  displayNumber,
  isSelected,
  onSelect,
  canvasWidth = BASE_CANVAS_W,
}) => {
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [canvasH, setCanvasH] = useState(Math.round(canvasWidth * 1.414));
  const [isHovered, setIsHovered] = useState(false);

  const { userRotation } = page;
  const isRotated = userRotation !== 0;

  useEffect(() => {
    if (!pdfDoc) return;
    setRendered(false);
    setCanvasH(Math.round(canvasWidth * 1.414));
    let cancelled = false;

    const render = async () => {
      try {
        const pdfPage = await pdfDoc.getPage(page.originalIndex + 1);
        // Scale to canvasWidth after rotation (dimensions may swap at 90°/270°)
        const vpForScale = pdfPage.getViewport({ scale: 1, rotation: userRotation });
        const scale = canvasWidth / vpForScale.width;
        const viewport = pdfPage.getViewport({ scale, rotation: userRotation });
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setCanvasH(viewport.height);
        await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (!cancelled) setRendered(true);
      } catch { /* non-fatal */ }
    };

    render();
    return () => { cancelled = true; };
  }, [pdfDoc, page.originalIndex, canvasWidth, userRotation]);

  const borderColor = isSelected
    ? 'blue.400'
    : isRotated
    ? 'orange.200'
    : isHovered
    ? 'gray.300'
    : 'gray.200';

  const bg = isSelected ? 'blue.50' : 'white';

  return (
    <Box
      bg={bg}
      borderWidth="2px"
      borderColor={borderColor}
      boxShadow={
        isSelected
          ? '0 0 0 3px rgba(66,153,225,0.18), 0 4px 12px rgba(0,0,0,0.10)'
          : '0 1px 4px rgba(0,0,0,0.06)'
      }
      borderRadius="14px"
      overflow="visible"
      cursor="pointer"
      position="relative"
      transition="border-color 0.15s, box-shadow 0.15s, background 0.15s"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(page.id)}
      role="button"
      aria-pressed={isSelected}
      aria-label={`Page ${displayNumber}${isSelected ? ', selected' : ''}${isRotated ? `, rotated ${userRotation}°` : ''}`}
    >
      {/* ── Selection circle ── */}
      <Box
        position="absolute"
        top={2}
        left={2}
        zIndex={4}
        w={5}
        h={5}
        borderRadius="full"
        borderWidth="2px"
        borderColor={isSelected ? 'blue.500' : 'blue.300'}
        bg={isSelected ? 'blue.500' : 'white'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        opacity={isSelected || isHovered ? 1 : 0}
        transition="all 0.15s"
        pointerEvents={isSelected || isHovered ? 'auto' : 'none'}
        cursor="pointer"
        onClick={(e) => { e.stopPropagation(); onSelect(page.id); }}
        _hover={{ borderColor: 'blue.400', transform: 'scale(1.5)' }}
      >
        {isSelected && <Icon as={MdCheck} color="white" boxSize={3} />}
      </Box>

      {/* ── Rotation badge ── */}
      <AnimatePresence>
        {isRotated && (
          <MotionBox
            key={userRotation}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.15 }}
            position="absolute"
            top={1.5}
            right={1.5}
            zIndex={4}
            borderRadius="full"
            px={1.5}
            py="2px"
            bg="orange.50"
            border="1.5px solid"
            borderColor="orange.200"
            pointerEvents="none"
            userSelect="none"
          >
            <Text fontSize="9px" fontWeight="700" color="orange.600" lineHeight="1.2">
              {userRotation}°
            </Text>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* ── Canvas / preview ── */}
      <Box
        mt={3}
        bg="gray.50"
        borderTopLeftRadius="12px"
        borderTopRightRadius="12px"
        overflow="hidden"
        minH={`${canvasH}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {!rendered && (
          <Skeleton
            w={`${canvasWidth}px`}
            h={`${canvasH}px`}
            startColor="gray.100"
            endColor="gray.200"
            speed={1.2}
          />
        )}
        <canvas
          ref={canvasRef}
          style={{ display: rendered ? 'block' : 'none', maxWidth: '100%' }}
        />
      </Box>

      {/* ── Page number ── */}
      <Flex justify="center" pt={1} pb="4px" px={2} mt="-2px" transform="translateY(-4px)">
        <Text
          as="span"
          fontSize="sm"
          fontWeight="600"
          lineHeight="1"
          color={isSelected ? 'blue.500' : isRotated ? 'orange.500' : 'gray.500'}
          letterSpacing="0.03em"
          transition="color 0.15s"
        >
          {displayNumber}
        </Text>
      </Flex>

      {/* ── Rotation accent strip ── */}
      {isRotated && (
        <Box
          h="3px"
          bg="orange.300"
          borderBottomLeftRadius="12px"
          borderBottomRightRadius="12px"
          mt="-4px"
          mx="2px"
          mb="2px"
        />
      )}
    </Box>
  );
};

// ── UploadArea ────────────────────────────────────────────────────────────────

const UploadArea = ({ onFileDrop }) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    noClick: true,
    onDrop: (accepted) => { if (accepted[0]) onFileDrop(accepted[0]); },
  });

  return (
    <Flex flex={1} align="center" justify="center" p={8}>
      <Box
        {...getRootProps()}
        w="full"
        maxW="520px"
        bg={isDragActive ? 'blue.50' : 'white'}
        border="2px dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.200'}
        borderRadius="20px"
        p={10}
        textAlign="center"
        transition="all 0.2s"
        cursor="default"
      >
        <input {...getInputProps()} />
        <Flex
          w={16}
          h={16}
          borderRadius="18px"
          bg={isDragActive ? 'blue.100' : 'gray.100'}
          align="center"
          justify="center"
          mx="auto"
          mb={5}
          transition="background 0.2s"
        >
          <Icon
            as={MdRotateRight}
            boxSize={8}
            color={isDragActive ? 'blue.500' : 'gray.400'}
            transition="color 0.2s"
          />
        </Flex>

        <Text fontSize="xl" fontWeight="700" color="gray.800" mb={1}>
          {isDragActive ? 'Drop your PDF here' : 'Rotate your PDF'}
        </Text>
        <Text fontSize="sm" color="gray.500" mb={2}>
          Rotate one page, selected pages, or the entire PDF.
        </Text>
        <Text fontSize="sm" color="gray.400" mb={6}>
          Drag and drop a PDF, or click to browse your files
        </Text>

        <Button
          onClick={(e) => { e.stopPropagation(); open(); }}
          colorScheme="blue"
          size="md"
          borderRadius="10px"
          fontWeight="600"
          leftIcon={<MdUploadFile size={17} />}
          px={6}
        >
          Select PDF
        </Button>

        <Flex align="center" justify="center" gap={1.5} mt={6}>
          <Icon as={MdLock} boxSize={3} color="gray.400" />
          <Text fontSize="xs" color="gray.400">
            Processed locally in your browser — your file never leaves this device
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};

// ── ZoomControl ───────────────────────────────────────────────────────────────

const ZoomControl = ({ zoom, setZoom }) => (
  <HStack spacing={0.5}>
    <Tooltip label="Zoom out" openDelay={500} hasArrow>
      <IconButton
        icon={<MdRemove size={14} />}
        aria-label="Zoom out"
        size="xs"
        variant="ghost"
        colorScheme="gray"
        borderRadius="6px"
        onClick={() =>
          setZoom((z) => {
            const idx = ZOOM_PRESETS.indexOf(z);
            return idx > 0 ? ZOOM_PRESETS[idx - 1] : z;
          })
        }
        isDisabled={zoom <= ZOOM_PRESETS[0]}
      />
    </Tooltip>
    <Menu>
      <MenuButton
        as={Button}
        size="xs"
        variant="ghost"
        colorScheme="gray"
        rightIcon={<MdArrowDropDown size={14} />}
        borderRadius="6px"
        fontWeight="600"
        fontSize="xs"
        px={2}
        minW="58px"
      >
        {zoom}%
      </MenuButton>
      <MenuList minW="100px" shadow="lg" borderRadius="10px" py={1}>
        {ZOOM_PRESETS.map((p) => (
          <MenuItem
            key={p}
            fontSize="sm"
            fontWeight={zoom === p ? '700' : '400'}
            color={zoom === p ? 'blue.600' : 'gray.700'}
            onClick={() => setZoom(p)}
            borderRadius="6px"
            mx={1}
            px={3}
          >
            {p}%
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
    <Tooltip label="Zoom in" openDelay={500} hasArrow>
      <IconButton
        icon={<MdAdd size={14} />}
        aria-label="Zoom in"
        size="xs"
        variant="ghost"
        colorScheme="gray"
        borderRadius="6px"
        onClick={() =>
          setZoom((z) => {
            const idx = ZOOM_PRESETS.indexOf(z);
            return idx < ZOOM_PRESETS.length - 1 ? ZOOM_PRESETS[idx + 1] : z;
          })
        }
        isDisabled={zoom >= ZOOM_PRESETS[ZOOM_PRESETS.length - 1]}
      />
    </Tooltip>
  </HStack>
);

// ── ActionBar ─────────────────────────────────────────────────────────────────

const ActionBar = ({
  selectedCount,
  totalCount,
  onRotateSelectedLeft,
  onRotateSelectedRight,
  onRotateAllLeft,
  onRotateAllRight,
  onSelectAll,
  onDeselectAll,
}) => {
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <Flex
      position="sticky"
      top={0}
      zIndex={10}
      bg="white"
      borderBottomWidth="1px"
      borderColor="gray.100"
      px={6}
      py={2.5}
      gap={2}
      align="center"
      boxShadow="0 2px 8px rgba(0,0,0,0.04)"
      flexWrap="wrap"
    >
      {/* ── Rotate selected ── */}
      <Tooltip
        label={hasSelection ? 'Rotate selected pages left' : 'Select pages first'}
        openDelay={400}
        hasArrow
      >
        <Box>
          <Button
            size="sm"
            variant={hasSelection ? 'solid' : 'outline'}
            colorScheme={hasSelection ? 'blue' : 'gray'}
            borderRadius="8px"
            fontWeight="600"
            leftIcon={<MdRotateLeft size={16} />}
            isDisabled={!hasSelection}
            onClick={onRotateSelectedLeft}
            px={3}
          >
            Left
          </Button>
        </Box>
      </Tooltip>
      <Tooltip
        label={hasSelection ? 'Rotate selected pages right' : 'Select pages first'}
        openDelay={400}
        hasArrow
      >
        <Box>
          <Button
            size="sm"
            variant={hasSelection ? 'solid' : 'outline'}
            colorScheme={hasSelection ? 'blue' : 'gray'}
            borderRadius="8px"
            fontWeight="600"
            leftIcon={<MdRotateRight size={16} />}
            isDisabled={!hasSelection}
            onClick={onRotateSelectedRight}
            px={3}
          >
            Right
          </Button>
        </Box>
      </Tooltip>

      {/* ── Selection info ── */}
      <Box px={1}>
        {hasSelection ? (
          <Text fontSize="sm" fontWeight="600" color="blue.600" whiteSpace="nowrap">
            {selectedCount} page{selectedCount !== 1 ? 's' : ''} selected
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.400" fontStyle="italic" whiteSpace="nowrap">
            Select pages to rotate
          </Text>
        )}
      </Box>

      <Box flex={1} />

      {/* ── Selection controls ── */}
      <Button
        size="sm"
        variant="ghost"
        colorScheme="gray"
        borderRadius="8px"
        fontWeight="500"
        fontSize="xs"
        onClick={allSelected ? onDeselectAll : onSelectAll}
        px={3}
      >
        {allSelected ? 'Deselect All' : 'Select All'}
      </Button>

      {hasSelection && (
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          borderRadius="8px"
          fontWeight="500"
          fontSize="xs"
          leftIcon={<MdClose size={13} />}
          onClick={onDeselectAll}
          px={2}
        >
          Clear
        </Button>
      )}

      {/* ── Separator ── */}
      <Box w="1px" h={5} bg="gray.200" mx={1} flexShrink={0} />

      {/* ── Rotate all ── */}
      <Text fontSize="xs" color="gray.400" fontWeight="500" whiteSpace="nowrap">
        All pages:
      </Text>
      <Tooltip label="Rotate all pages left (−90°)" openDelay={400} hasArrow>
        <IconButton
          icon={<MdRotateLeft size={17} />}
          aria-label="Rotate all left"
          size="sm"
          variant="ghost"
          colorScheme="gray"
          borderRadius="8px"
          onClick={onRotateAllLeft}
        />
      </Tooltip>
      <Tooltip label="Rotate all pages right (+90°)" openDelay={400} hasArrow>
        <IconButton
          icon={<MdRotateRight size={17} />}
          aria-label="Rotate all right"
          size="sm"
          variant="ghost"
          colorScheme="gray"
          borderRadius="8px"
          onClick={onRotateAllRight}
        />
      </Tooltip>
    </Flex>
  );
};

// ── RotatePdf (main) ──────────────────────────────────────────────────────────

const RotatePdf = () => {
  const toast = useToast();

  // ── File state ──
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  // pages: { id, originalIndex, userRotation }
  const [pages, setPages] = useState([]);

  // ── Selection state ──
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── UI state ──
  const [zoom, setZoom] = useState(75);
  const [isExporting, setIsExporting] = useState(false);

  const canvasWidth = Math.round(BASE_CANVAS_W * (zoom / 100));

  // ── Derived ──
  const selectedCount = selectedIds.size;
  const rotatedCount = useMemo(() => pages.filter((p) => p.userRotation !== 0).length, [pages]);
  const hasRotations = rotatedCount > 0;
  const selectedAndRotatedCount = useMemo(
    () => pages.filter((p) => selectedIds.has(p.id) && p.userRotation !== 0).length,
    [pages, selectedIds]
  );

  // ── File loading ──
  const loadFile = useCallback(async (file) => {
    setIsLoadingFile(true);
    try {
      const buffer = await readFileAsArrayBuffer(file);
      const doc = await pdfjsLib.getDocument({ data: buffer.slice(0) }).promise;
      setPdfFile(file);
      setArrayBuffer(buffer);
      setPdfDoc(doc);
      setPages(
        Array.from({ length: doc.numPages }, (_, i) => ({
          id: `rp-${i}-${uid()}`,
          originalIndex: i,
          userRotation: 0,
        }))
      );
      setSelectedIds(new Set());
    } catch {
      toast({
        title: 'Could not open PDF',
        description: 'The file may be corrupted or password-protected.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoadingFile(false);
    }
  }, [toast]);

  const unload = useCallback(() => {
    if (pdfDoc) { try { pdfDoc.destroy(); } catch { /* ok */ } }
    setPdfFile(null);
    setPdfDoc(null);
    setArrayBuffer(null);
    setPages([]);
    setSelectedIds(new Set());
  }, [pdfDoc]);

  // ── Dropzone (hidden — for "change file" button) ──
  const { getRootProps: getDropRootProps, getInputProps: getDropInputProps } =
    useDropzone({
      accept: { 'application/pdf': ['.pdf'] },
      multiple: false,
      noClick: true,
      onDrop: (accepted) => { if (accepted[0]) loadFile(accepted[0]); },
    });

  // ── Selection helpers ──
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => setSelectedIds(new Set(pages.map((p) => p.id))), [pages]);
  const deselectAll = useCallback(() => setSelectedIds(new Set()), []);

  // ── Rotation helpers ──
  const applyRotationToIds = useCallback((ids, delta) => {
    setPages((prev) =>
      prev.map((p) =>
        ids.has(p.id)
          ? { ...p, userRotation: normalizeRotation(p.userRotation + delta) }
          : p
      )
    );
  }, []);

  const rotateSelectedLeft = useCallback(() => {
    if (selectedIds.size > 0) applyRotationToIds(selectedIds, -90);
  }, [selectedIds, applyRotationToIds]);

  const rotateSelectedRight = useCallback(() => {
    if (selectedIds.size > 0) applyRotationToIds(selectedIds, 90);
  }, [selectedIds, applyRotationToIds]);

  const rotateAllLeft = useCallback(() => {
    applyRotationToIds(new Set(pages.map((p) => p.id)), -90);
  }, [pages, applyRotationToIds]);

  const rotateAllRight = useCallback(() => {
    applyRotationToIds(new Set(pages.map((p) => p.id)), 90);
  }, [pages, applyRotationToIds]);

  const resetSelected = useCallback(() => {
    setPages((prev) =>
      prev.map((p) => (selectedIds.has(p.id) ? { ...p, userRotation: 0 } : p))
    );
  }, [selectedIds]);

  const resetAll = useCallback(() => {
    setPages((prev) => prev.map((p) => ({ ...p, userRotation: 0 })));
  }, []);

  // ── Export ──
  const handleExport = useCallback(async () => {
    if (!pdfFile || isExporting) return;
    setIsExporting(true);
    try {
      await exportRotatedPdf(arrayBuffer, pages, pdfFile.name);
      toast({
        title: hasRotations ? 'PDF exported!' : 'PDF exported (no changes)',
        description: hasRotations
          ? `${rotatedCount} page${rotatedCount !== 1 ? 's' : ''} rotated.`
          : 'No rotation changes were applied.',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsExporting(false);
    }
  }, [pdfFile, isExporting, arrayBuffer, pages, hasRotations, rotatedCount, toast]);

  // ── Empty state ──
  if (!pdfFile && !isLoadingFile) {
    return <UploadArea onFileDrop={loadFile} />;
  }

  // ── Loading state ──
  if (isLoadingFile) {
    return (
      <Flex flex={1} align="center" justify="center" direction="column" gap={4}>
        <Box
          w={14}
          h={14}
          borderRadius="16px"
          bg="blue.50"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={MdPictureAsPdf} boxSize={7} color="blue.400" />
        </Box>
        <VStack spacing={1}>
          <Text fontWeight="600" color="gray.700">Loading PDF…</Text>
          <Text fontSize="sm" color="gray.400">Generating page previews</Text>
        </VStack>
      </Flex>
    );
  }

  // ── Workspace ──
  return (
    <Flex flex={1} overflow="hidden" minH={0}>

      {/* ── Main scrollable area ── */}
      <Box flex={1} overflow="auto" minW={0} display="flex" flexDirection="column">

        {/* ── Sticky action bar ── */}
        <ActionBar
          selectedCount={selectedCount}
          totalCount={pages.length}
          onRotateSelectedLeft={rotateSelectedLeft}
          onRotateSelectedRight={rotateSelectedRight}
          onRotateAllLeft={rotateAllLeft}
          onRotateAllRight={rotateAllRight}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
        />

        {/* ── Scrollable content ── */}
        <Box flex={1} p={6}>

          {/* Grid header row */}
          <Flex align="center" justify="space-between" mb={4}>
            <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
              {pages.length} page{pages.length !== 1 ? 's' : ''}
              {rotatedCount > 0 && (
                <Text as="span" fontWeight="400" color="orange.400">
                  {' '}· {rotatedCount} rotated
                </Text>
              )}
              {selectedCount > 0 && (
                <Text as="span" fontWeight="400" color="blue.400">
                  {' '}· {selectedCount} selected
                </Text>
              )}
            </Text>
            <ZoomControl zoom={zoom} setZoom={setZoom} />
          </Flex>

          {/* Thumbnail grid */}
          <Box
            display="grid"
            gridTemplateColumns={`repeat(auto-fill, minmax(${canvasWidth + 20}px, max-content))`}
            gap={3}
          >
            {pages.map((page, idx) => (
              <RotatePageTile
                key={page.id}
                page={page}
                pdfDoc={pdfDoc}
                displayNumber={idx + 1}
                isSelected={selectedIds.has(page.id)}
                onSelect={toggleSelect}
                canvasWidth={canvasWidth}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Right panel ── */}
      <Box
        w="240px"
        minW="240px"
        borderLeftWidth="1px"
        borderColor="gray.100"
        bg="white"
        overflow="auto"
        p={5}
        display={{ base: 'none', xl: 'flex' }}
        flexDirection="column"
        gap={4}
        flexShrink={0}
      >
        {/* Hidden dropzone input */}
        <Box {...getDropRootProps()} display="none">
          <input {...getDropInputProps()} />
        </Box>

        {/* ── File info ── */}
        <Flex align="center" gap={3} pb={4} borderBottomWidth="1px" borderColor="gray.100">
          <Flex
            w={9}
            h={9}
            borderRadius="10px"
            bg="blue.50"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={MdFilePresent} color="blue.500" boxSize={5} />
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontWeight="600" color="gray.800" fontSize="sm" isTruncated>
              {pdfFile.name}
            </Text>
            <Text fontSize="xs" color="gray.400" mt={0.5}>
              {pages.length} page{pages.length !== 1 ? 's' : ''}
            </Text>
          </Box>
        </Flex>

        {/* ── Rotation summary ── */}
        <Box bg="gray.50" borderRadius="12px" p={4}>
          {hasRotations ? (
            <>
              <Text fontSize="2xl" fontWeight="800" color="orange.500" lineHeight="1">
                {rotatedCount}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                page{rotatedCount !== 1 ? 's' : ''} rotated
                {pages.length > 0 && (
                  <Text as="span" color="gray.400"> of {pages.length}</Text>
                )}
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="2xl" fontWeight="800" color="gray.300" lineHeight="1">—</Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                No rotations applied yet
              </Text>
            </>
          )}
        </Box>

        {/* ── Reset controls ── */}
        <AnimatePresence>
          {(hasRotations || selectedCount > 0) && (
            <MotionFlex
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              direction="column"
              gap={1.5}
            >
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.1em">
                Reset
              </Text>
              {selectedAndRotatedCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  borderRadius="8px"
                  fontWeight="500"
                  fontSize="xs"
                  onClick={resetSelected}
                  justifyContent="flex-start"
                >
                  Reset selected ({selectedAndRotatedCount})
                </Button>
              )}
              {hasRotations && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  borderRadius="8px"
                  fontWeight="500"
                  fontSize="xs"
                  onClick={resetAll}
                  justifyContent="flex-start"
                >
                  Reset all rotations
                </Button>
              )}
            </MotionFlex>
          )}
        </AnimatePresence>

        <Divider borderColor="gray.100" />

        {/* ── Export & Change file ── */}
        <VStack align="stretch" spacing={2}>
          <Button
            colorScheme="blue"
            size="md"
            borderRadius="10px"
            fontWeight="700"
            leftIcon={<MdDownload size={17} />}
            isDisabled={!pdfFile}
            isLoading={isExporting}
            loadingText="Exporting…"
            onClick={handleExport}
            boxShadow={pdfFile ? '0 2px 8px rgba(66,153,225,0.30)' : 'none'}
            transition="box-shadow 0.2s"
          >
            Export PDF
          </Button>

          {!hasRotations && pdfFile && (
            <Text fontSize="10px" color="gray.400" textAlign="center">
              No rotations yet — export will be identical to source
            </Text>
          )}

          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            borderRadius="8px"
            fontWeight="500"
            onClick={unload}
          >
            Change file
          </Button>
        </VStack>
      </Box>
    </Flex>
  );
};

export default RotatePdf;
